import { Locale, eachDayOfInterval, eachMonthOfInterval, formatDistanceStrict, formatDuration, intervalToDuration } from 'date-fns';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { getLocale } from '../locales/locales';

export const formatDate = (date: Date, formatStr = 'P') => {
  return format(date, formatStr, {
    locale: getFormattedLocale(),
  });
};

export const formatDateStr = (date: string, formatStr = 'P') => {
  return format(parseDate(date), formatStr, {
    locale: getFormattedLocale(),
  });
};

export const formatTime = (date: Date) => {
  return date.toLocaleTimeString(getFormattedLocale()?.code, { hour: '2-digit', minute: '2-digit' });
};

export const ignoreDateTimezone = (d: Date): Date => {
  const date = new Date(d);
  date.setHours(0, -1 * date.getTimezoneOffset(), 0, 0);
  return date;
};

export const parseDate = (date: string, ignoreTimezone = false) => {
  const parsedDate = parseISO(date);
  return ignoreTimezone ? ignoreDateTimezone(parsedDate) : parsedDate;
};

declare global {
  interface Window {
    dateFnsLocale: Locale | undefined;
  }
}

export const getFormattedLocale = (): Locale | undefined => {
  return typeof window !== 'undefined' ? window.dateFnsLocale : undefined;
};

// TODO: The util is a copy from /team-s/fleet-management/frontend/util/src/format.ts
// Move all methods from that file to this file

export interface TimeAgoOptions {
  locale?: Locale;
  unit?: 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year';
  addSuffix?: boolean;
}

export function formatDateToTimeAgo(date: Date, options?: TimeAgoOptions) {
  return formatDistanceStrict(date, new Date(), {
    addSuffix: options?.addSuffix !== false,
    locale: options?.locale ?? getFormattedLocale(),
    unit: options?.unit,
  });
}

export function formatIntlDate(date?: Date | string, dateStyle: 'long' | 'full' | 'medium' | 'short' = 'long') {
  const _date = date ? new Date(date) : new Date();
  return new Intl.DateTimeFormat(getLocale().toString(), {
    dateStyle,
  }).format(_date);
}

export function getDaysNamesForLocale(locale = getLocale().toString(), weekday: 'long' | 'short' | 'narrow' = 'long') {
  const { format } = new Intl.DateTimeFormat(locale, { weekday });
  return [...Array(7).keys()].map((day) => format(new Date(7, 0, day)));
}

export const convertSecondsIntervalToMinAndSec = (seconds = 0) => {
  const duration = intervalToDuration({ start: 0, end: seconds * 1000 });
  const padding = (num) => String(num).padStart(2, '0');

  return formatDuration(duration, {
    format: ['hours', 'minutes'],
    zero: true,
    delimiter: ':',
    locale: {
      formatDistance: (_token, count) => padding(count),
    },
  });
};

export const convertMinAndSecToSeconds = (timeString: string) => {
  const [hours, minutes] = timeString.split(':').map(Number);

  if (isNaN(hours) || isNaN(minutes) || hours < 0 || minutes < 0 || minutes > 59) {
    return NaN;
  }

  return hours * 3600 + minutes * 60;
};

export const januaryToDecember = (year = 2023): Date[] =>
  eachMonthOfInterval({
    start: new Date(`${year}-01-02`),
    end: new Date(`${year}-12-30`),
  });
export const mondayToSunday: Date[] = eachDayOfInterval({
  start: new Date('2023-01-02'),
  end: new Date('2023-01-08'),
});
